import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Tab, Nav } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { createFarm } from '../../services/Farm/farmService';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import InputField from '../Utils/InputField';
import UFSelect from '../Utils/UFSelect';
import DocumentInput from '../Utils/DocumentInput';
import PhoneInput from '../Utils/PhoneInput';
import ZipCodeInput from '../Utils/ZipCodeInput';
import PersonInput from '../Utils/PersonInput';

const CreateFarmModal = ({ show, handleClose, refreshFarms, onCreated }) => {
  const { register, handleSubmit, formState: { errors }, reset, control, setValue } = useForm();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeKey, setActiveKey] = useState('general');

  const onSubmit = async (data) => {
    try {
      const farmData = {
        ...data,
        personId: data.personId?.id,
        owner: data.personId?.name || '',
      };
      const created = await createFarm(farmData);

      if (onCreated) {
        onCreated({
          id: created.uuid,
          name: created.name,
        });
      } else {
        setSuccessMessage('Fazenda criada com sucesso!');
        setShowSuccessModal(true);
        setValue('personId', { name: '', id: null });
      }

      reset();
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao criar fazenda.');
      setShowErrorModal(true);
    }
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshFarms();
    handleClose();
  };

  useEffect(() => {
    if (show) {
      reset();
    }
  }, [show, reset]);

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Cadastrar Fazenda</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.stopPropagation();
              handleSubmit(onSubmit)(e);
            }}
          >
            <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="general">Informações Gerais</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="address">Endereço</Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="general">
                  <Row>
                    <Col md={8}>
                      <InputField
                        label="Nome da Fazenda"
                        name="name"
                        placeholder="Digite o nome"
                        register={register}
                        errors={errors}
                        rules={{ required: 'Nome é obrigatório' }}
                      />
                    </Col>
                    <Col md={4}>
                      <DocumentInput
                        label="CPF/CNPJ"
                        name="document"
                        register={register}
                        errors={errors}
                        rules={{ required: 'Documento é obrigatório' }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <InputField
                        label="Inscr. Estadual"
                        name="registration"
                        placeholder="Digite a Inscrição Estadual"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={4}>
                      <PhoneInput
                        label="Telefone Comercial"
                        name="businessPhone"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={4}>
                      <PhoneInput
                        label="Telefone Celular"
                        name="cellPhone"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <InputField
                        label="Email"
                        name="mail"
                        placeholder="Digite o email"
                        register={register}
                        errors={errors}
                        rules={{
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: 'Endereço de email inválido',
                          }
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <InputField
                        label="Observações"
                        name="observations"
                        placeholder="Digite observações"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Controller
                        name="personId"
                        control={control}
                        render={({ field, fieldState }) => (
                          <PersonInput
                            value={field.value}
                            onChange={(selectedPerson) => setValue('personId', selectedPerson)}
                            label="Proprietário"
                            isInvalid={!!fieldState.error}
                            createdNew={true}
                            type={['isCustomer', 'isProducer', 'isSupplier']}
                          />
                        )}
                      />
                    </Col>
                    <Col md={6}>
                      <InputField
                        label="Arrendatário"
                        name="tenant"
                        placeholder="Informe o tipo do arrendatário"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                </Tab.Pane>
                <Tab.Pane eventKey="address">
                  <Row>
                    <Col md={6}>
                      <InputField
                        label="Endereço"
                        name="address"
                        placeholder="Digite o endereço"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={6}>
                      <InputField
                        label="Bairro"
                        name="district"
                        placeholder="Digite o bairro"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={5}>
                      <InputField
                        label="Cidade"
                        name="city"
                        placeholder="Digite a cidade"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={4}>
                      <UFSelect
                        label="Estado"
                        name="state"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                    <Col md={3}>
                      <ZipCodeInput
                        label="CEP"
                        name="zipCode"
                        register={register}
                        errors={errors}
                      />
                    </Col>
                  </Row>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
            <Button variant="primary" className="button-custom w-100 mt-3" type="submit">
              Cadastrar
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default CreateFarmModal;